import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCar,
  faXmark,
  faCamera,
  faChevronRight,
  faBuilding,
  faLocationDot,
  faIdCard,
  faPhone,
  faParking,
} from '@fortawesome/free-solid-svg-icons';
import NavigationSecurity from '../../Layouts/Navigation/NavigationSecurity';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';
import ImageCropDialog from '../../Profile/ImageCropDialog';
import { getCroppedImg } from '../../Profile/cropUtils';

export default function SecurityVehicleControl() {
  const [towers, setTowers] = useState([]);
  const [selectedTower, setSelectedTower] = useState('');
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  const fileInputRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const pendingFileRef = useRef(null);

  useAutoDismissSnack(openSnack, setOpenSnack);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/towers`)
      .then((res) => setTowers(res.data))
      .catch(() => showSnack('error', 'Error al cargar las torres'));
  }, []);

  useEffect(() => {
    if (!selectedTower) {
      setApartments([]);
      setSelectedApartment('');
      setVehicles([]);
      setSelectedVehicle(null);
      return;
    }
    axios
      .get(`${BASE_URL}/getApartmentsData/${selectedTower}`)
      .then((res) => setApartments(res.data))
      .catch(() => showSnack('error', 'Error al cargar los apartamentos'));
    setSelectedApartment('');
    setVehicles([]);
    setSelectedVehicle(null);
  }, [selectedTower]);

  useEffect(() => {
    if (!selectedApartment) {
      setVehicles([]);
      setSelectedVehicle(null);
      return;
    }
    setLoadingVehicles(true);
    setSelectedVehicle(null);
    axios
      .get(`${BASE_URL}/vehicles/apartment/${selectedApartment}`)
      .then((res) => setVehicles(res.data))
      .catch(() => showSnack('error', 'Error al cargar los vehículos'))
      .finally(() => setLoadingVehicles(false));
  }, [selectedApartment]);

  const showSnack = (status, message) => {
    setSnackStatus(status);
    setSnackMessage(message);
    setOpenSnack(true);
  };

  const handleUploadPhoto = () => {
    if (!selectedVehicle) return;
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const onFileSelected = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    pendingFileRef.current = f;
    const url = URL.createObjectURL(f);
    setCropSrc(url);
    setCropOpen(true);
  };

  const onCropCancel = () => {
    setCropOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    pendingFileRef.current = null;
  };

  const onCropDone = (blob) => {
    setCropOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (!selectedVehicle) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    const filename = `vehicle-${Date.now()}.jpg`;
    formData.append('foto', blob, filename);
    axios
      .post(`${BASE_URL}/vehicles/${selectedVehicle.id_vehiculo}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => {
        showSnack('success', 'Foto actualizada correctamente');
        const updatedFoto = res.data.foto;
        setVehicles((prev) => prev.map((v) => (v.id_vehiculo === selectedVehicle.id_vehiculo ? { ...v, foto: updatedFoto } : v)));
        setSelectedVehicle((prev) => ({ ...prev, foto: updatedFoto }));
      })
      .catch(() => showSnack('error', 'Error al subir la foto'))
      .finally(() => setUploadingPhoto(false));
  };

  const handleCropComplete = async (croppedAreaPixels) => {
    try {
      const croppedBlob = await getCroppedImg(cropSrc, croppedAreaPixels);
      onCropDone(croppedBlob);
    } catch (error) {
      console.error('Error al recortar imagen:', error);
      showSnack('error', 'Error al procesar imagen');
    }
  };

  const selectedTowerObj = towers.find((t) => String(t.id_torre) === String(selectedTower));
  const selectedAptObj = apartments.find((a) => String(a.id_apartamento) === String(selectedApartment));

  return (
    <>
      <NavigationSecurity />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {openSnack && (
            <div
              className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}
              style={{ zIndex: 9999 }}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold">{snackMessage}</span>
                <button onClick={() => setOpenSnack(false)} className="ml-2 hover:bg-white/20 rounded p-1 transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <div className="mb-8 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
              <FontAwesomeIcon icon={faCar} className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Control de Vehículos</h1>
              <p className="text-sm text-gray-500">Selecciona torre y apartamento para ver los vehículos registrados</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FontAwesomeIcon icon={faBuilding} className="mr-2 text-blue-500" /> Torre
              </label>
              <select value={selectedTower} onChange={(e) => setSelectedTower(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50">
                <option value="">-- Seleccionar torre --</option>
                {towers.map((t) => (
                  <option key={t.id_torre} value={t.id_torre}>{t.nombre}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FontAwesomeIcon icon={faLocationDot} className="mr-2 text-blue-500" /> Apartamento
              </label>
              <select value={selectedApartment} onChange={(e) => setSelectedApartment(e.target.value)} disabled={!selectedTower} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 disabled:opacity-50">
                <option value="">-- Seleccionar apartamento --</option>
                {apartments.map((a) => (
                  <option key={a.id_apartamento} value={a.id_apartamento}>Apto {a.num_apartamento}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-700 mb-4">Vehículos
                {selectedAptObj && (<span className="ml-2 text-sm font-normal text-gray-500"> — Apto {selectedAptObj.num_apartamento}{selectedTowerObj && `, ${selectedTowerObj.nombre}`}</span>)}
              </h2>

              {!selectedApartment && (<div className="text-center py-10 text-gray-400"><FontAwesomeIcon icon={faCar} className="h-12 w-12 mx-auto mb-3 opacity-30" /><p className="text-sm">Selecciona un apartamento para ver sus vehículos</p></div>)}

              {selectedApartment && loadingVehicles && (<div className="text-center py-10 text-gray-400 text-sm">Cargando vehículos...</div>)}

              {selectedApartment && !loadingVehicles && vehicles.length === 0 && (<div className="text-center py-10 text-gray-400"><FontAwesomeIcon icon={faCar} className="h-12 w-12 mx-auto mb-3 opacity-30" /><p className="text-sm">No hay vehículos registrados en este apartamento</p></div>)}

              <div className="space-y-3">
                {vehicles.map((v) => (
                  <button key={v.id_vehiculo} onClick={() => setSelectedVehicle(v)} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${selectedVehicle?.id_vehiculo === v.id_vehiculo ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`}>
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      {v.foto ? (<img src={`${BASE_URL}${v.foto}`} alt={v.placa} className="w-full h-full object-cover" />) : (<FontAwesomeIcon icon={faCar} className="h-6 w-6 text-gray-400" />)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800">{v.placa}</p>
                      <p className="text-sm text-gray-600 truncate">{v.marca} {v.modelo}</p>
                      <p className="text-xs text-gray-400 truncate">{v.usuario_nombre} {v.usuario_apellido}</p>
                    </div>
                    <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6">
              {!selectedVehicle ? (
                <div className="text-center py-16 text-gray-400"><FontAwesomeIcon icon={faCar} className="h-16 w-16 mx-auto mb-4 opacity-20" /><p className="text-sm">Selecciona un vehículo para ver su información</p></div>
              ) : (
                <>
                  <h2 className="text-lg font-bold text-gray-700 mb-5">Detalle del Vehículo</h2>
                  <div className="rounded-xl mb-6 flex items-center justify-center overflow-hidden w-full" style={{ maxWidth: 560 }}>
                    <div className="relative w-full" style={{ paddingTop: '100%' }}>
                      {selectedVehicle.foto ? (
                        <img src={`${BASE_URL}${selectedVehicle.foto}`} alt={selectedVehicle.placa} className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-blue-100 rounded-xl">
                          <FontAwesomeIcon icon={faCar} className="h-20 w-20 text-blue-300" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mb-6">
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFileSelected} />
                    <button onClick={handleUploadPhoto} disabled={uploadingPhoto} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap">
                      <FontAwesomeIcon icon={faCamera} className="h-4 w-4" /> {uploadingPhoto ? 'Subiendo...' : 'Subir foto'}
                    </button>
                  </div>

                  {cropOpen && cropSrc && (
                    <ImageCropDialog
                      image={cropSrc}
                      onCropComplete={handleCropComplete}
                      onCancel={onCropCancel}
                    />
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <FontAwesomeIcon icon={faIdCard} className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Placa</p>
                        <p className="font-bold text-gray-800 text-lg tracking-widest">{selectedVehicle.placa}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-xl"><p className="text-xs text-gray-500">Marca</p><p className="font-semibold text-gray-800">{selectedVehicle.marca || '—'}</p></div>
                      <div className="p-3 bg-gray-50 rounded-xl"><p className="text-xs text-gray-500">Modelo</p><p className="font-semibold text-gray-800">{selectedVehicle.modelo || '—'}</p></div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500">Propietario</p>
                      <p className="font-semibold text-gray-800">{selectedVehicle.usuario_nombre} {selectedVehicle.usuario_apellido}</p>
                      {selectedVehicle.identificacion && <p className="text-xs text-gray-500 mt-0.5">CC {selectedVehicle.identificacion}</p>}
                    </div>
                    {selectedVehicle.telefono && (<div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"><FontAwesomeIcon icon={faPhone} className="h-4 w-4 text-blue-500 flex-shrink-0" /><div><p className="text-xs text-gray-500">Teléfono</p><p className="font-semibold text-gray-800">{selectedVehicle.telefono}</p></div></div>)}
                    {selectedVehicle.num_parqueadero && (<div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"><FontAwesomeIcon icon={faParking} className="h-5 w-5 text-blue-500 flex-shrink-0" /><div><p className="text-xs text-gray-500">Parqueadero</p><p className="font-semibold text-gray-800">#{selectedVehicle.num_parqueadero}</p></div></div>)}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
